import { Component, computed, inject } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { MatButtonModule } from '@angular/material/button';
import { MatDialog } from '@angular/material/dialog';
import { MatMenuModule } from '@angular/material/menu';
import { MatTooltipModule } from '@angular/material/tooltip';
import { HasPermissionDirective } from '@core/directives/has-permission.directive';
import { IRole } from '@core/interfaces/role.interface';
import { ITeam } from '@core/interfaces/team.interface';
import { IUser } from '@core/interfaces/user.interface';
import { RoleService } from '@core/services/role.service';
import { SnackbarService } from '@core/services/snackbar.service';
import { TeamService } from '@core/services/team.service';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { AddCustomRoleDialogComponent } from '@pages/settings/user-management/users-tab/add-custom-role-dialog/add-custom-role-dialog.component';
import { IAddCustomRoleDialogData } from '@pages/settings/user-management/users-tab/add-custom-role-dialog/add-custom-role-dialog.interface';
import { AddTeamDialogComponent } from '@pages/settings/user-management/users-tab/add-team-dialog/add-team-dialog.component';
import { IAddTeamDialogData } from '@pages/settings/user-management/users-tab/add-team-dialog/add-team-dialog.interface';
import { BaseComponent } from '@shared/components/base.component';
import { ConfirmDialogComponent, IConfirmDialogData } from '@shared/components/confirm-dialog/confirm-dialog.component';
import { CreateEntityButtonComponent } from '@shared/components/create-entity-button/create-entity-button.component';
import { DatatableComponent } from '@shared/components/datatable/datatable.component';
import {
  IDatatableConfiguration,
  IRowClickEvent,
  TDatatableColumn,
} from '@shared/components/datatable/datatable.interface';
import { IconBadgeListComponent } from '@shared/components/icon-badge-list/icon-badge-list.component';
import { IIconData } from '@shared/components/icon-badge-list/icon-badge-list.interface';
import { IconBadgeComponent } from '@shared/components/icon-badge/icon-badge.component';
import { IconComponent } from '@shared/components/icon/icon.component';
import { ISkeletonLoaderConfig } from '@shared/components/skeleton-loader/skeleton-loader.interface';
import { getRoleIcon } from '@utilities/role-icon.util';
import { translateRoleName } from '@utilities/role-name.util';
import { roleExceedsPermissions } from '@utilities/role-permission-comparison.util';
import { Observable } from 'rxjs';
import { UserManagementSharedDataStore } from '../user-management-shared-data.store';
import { IUserManagementRoleTeamRow } from './user-management-roles-teams-tab.interface';

/**
 * "Roles & Teams" tab - two client-side, no-header, no-pagination `app-datatable` instances (all
 * roles/teams/users come from the shared UserManagementSharedDataStore, loaded once and reused
 * across tabs/dialogs - see that service's doc comment) each rendering one row per role/team: an
 * identity column (icon + name + user count + avatar cluster) and an actions column (a 3-dot
 * menu). Both row shapes are unified into `IUserManagementRoleTeamRow` so a single pair of
 * `ng-template`s serves both tables.
 *
 * Row actions:
 * - A built-in role (Admin/Member, `isProtected`) only offers "View permissions" - it can't be
 *   edited or deleted (enforced server-side too, see `_validate_permission_subset`/protected-role
 *   checks in campaign-management-api).
 * - A custom role offers "Edit permissions" (opens the same dialog in 'edit' mode) and "Delete" -
 *   except when it exceeds the current user's own permissions (`!canManageRole`), where the same
 *   menu item reads "View permissions" and opens the dialog in 'view' mode instead (see
 *   `onRoleMenuClick`'s `mode` derivation).
 * - A team offers "Edit team" and "Delete".
 *
 * Create/edit/delete all mutate the shared service directly (`upsertRole`/`upsertTeam`/
 * `removeRole`/`removeTeam`) so every other consumer - the Users tab's filters and Change
 * Role/Team dialogs included - reflects the change immediately without re-fetching.
 */
@Component({
  imports: [
    CreateEntityButtonComponent,
    DatatableComponent,
    HasPermissionDirective,
    IconBadgeComponent,
    IconBadgeListComponent,
    IconComponent,
    MatButtonModule,
    MatMenuModule,
    MatTooltipModule,
    TranslateModule,
  ],
  selector: 'app-user-management-roles-teams-tab',
  styleUrl: './user-management-roles-teams-tab.component.scss',
  templateUrl: './user-management-roles-teams-tab.component.html',
})
export class UserManagementRolesTeamsTabComponent extends BaseComponent {
  public readonly sharedData = inject(UserManagementSharedDataStore);

  /** Keeps both tables sorted by name ascending by default (applied directly to the underlying
   * data, not just as an initial sort state the table's own sort UI could override) -
   * `sensitivity: 'base'` gives a locale-aware, case-insensitive compare. */
  private readonly nameCollator = new Intl.Collator(undefined, { sensitivity: 'base' });

  /** The currently logged-in user's own permissions - not the role being deleted. Reactive so a
   * permission change elsewhere in the session is reflected immediately - see
   * AddCustomRoleDialogComponent's identical field for the same reasoning. */
  private readonly currentUserPermissions = toSignal(this.permissionService.currentUserPermissions$, {
    initialValue: this.permissionService.currentUserPermissions,
  });

  public readonly roleRows = computed<IUserManagementRoleTeamRow[] | null>(() => {
    const roles = this.sharedData.roles$();
    const users = this.sharedData.users$();
    const userInvitations = this.sharedData.userInvitations$();

    if (!roles || !users || !userInvitations) {
      return null;
    }

    return roles
      .map((role): IUserManagementRoleTeamRow => {
        const roleUsers = users.filter((user) => this.membershipRoleId(user) === role.id);
        const pendingInvitationCount = userInvitations.filter(
          (userInvitation) => !userInvitation.createdUserId && userInvitation.targetRole === role.id,
        ).length;

        return {
          avatars: this.toAvatars(roleUsers),
          icon: getRoleIcon(role),
          id: role.id,
          isProtected: !role.userGroupId,
          kind: 'role',
          name: translateRoleName(role, this.translate),
          pendingInvitationCount,
          role,
          userCount: roleUsers.length,
        };
      })
      .sort((a, b) => this.nameCollator.compare(a.name, b.name));
  });

  public readonly teamRows = computed<IUserManagementRoleTeamRow[] | null>(() => {
    const teams = this.sharedData.teams$();
    const users = this.sharedData.users$();
    const userInvitations = this.sharedData.userInvitations$();

    if (!teams || !users || !userInvitations) {
      return null;
    }

    return teams
      .map((team): IUserManagementRoleTeamRow => {
        const teamUsers = users.filter((user) => user.teamId === team.id);
        const pendingInvitationCount = userInvitations.filter(
          (userInvitation) => !userInvitation.createdUserId && userInvitation.teamId === team.id,
        ).length;

        return {
          avatars: this.toAvatars(teamUsers),
          icon: team.iconId ?? null,
          id: team.id,
          isProtected: false,
          kind: 'team',
          name: team.name,
          pendingInvitationCount,
          team,
          userCount: teamUsers.length,
        };
      })
      .sort((a, b) => this.nameCollator.compare(a.name, b.name));
  });

  /** Deliberately separate per-table flags, even though both are fed by the same shared service -
   * `roles$()`/`teams$()`/`users$()`/`userInvitations$()` don't all resolve at the same time (a
   * single shared flag previously kept the Roles table's skeleton up while only the Teams table's
   * dependencies were still pending, and vice versa). Each only depends on its own rows computed
   * being ready. */
  public readonly roleRowsLoading = computed<boolean>(() => this.roleRows() === null);
  public readonly teamRowsLoading = computed<boolean>(() => this.teamRows() === null);

  public datatableColumns: TDatatableColumn<IUserManagementRoleTeamRow, string>[] = [
    { dataViewStrategy: (row: IUserManagementRoleTeamRow) => row.name, id: 'name' },
    { dataViewStrategy: () => '', id: 'actions' },
  ];

  public readonly datatableConfig: IDatatableConfiguration<IUserManagementRoleTeamRow> = {
    isClientSide: true,
    isHeaderRowEnabled: false,
    isPaginationEnabled: false,
    isRowClickEnabled: true,
    isSearchEnabled: false,
    rowColor: 'var(--shikenso-gray-base)',
  };

  public readonly skeletonLoaderConfig: ISkeletonLoaderConfig = {
    columns: [
      {
        borderRadius: '4px',
        circleSize: '40px',
        className: 'w-[92%]',
        columnPadding: '0 8px',
        hasText: true,
        height: '32px',
        rowPadding: '8px 0',
        textWidth: '200px',
        type: 'icon',
      },
      { borderRadius: '100vw', circleSize: '24px', className: 'w-[8%] flex justify-start', type: 'icon', width: '0' },
    ],
    containerClass: 'skeleton-container mt-0.5',
    rows: 3,
    showColumns: false,
    showFilters: false,
    showHeaderControls: false,
    showPaginator: false,
    showSearchField: false,
  };

  private readonly dialog = inject(MatDialog);
  private readonly translate = inject(TranslateService);
  private readonly roleService = inject(RoleService);
  private readonly teamService = inject(TeamService);
  private readonly snackbarService = inject(SnackbarService);

  constructor() {
    super();
  }

  public onAddCustomRoleClick(): void {
    this.dialog
      .open<AddCustomRoleDialogComponent, IAddCustomRoleDialogData, IRole | undefined>(AddCustomRoleDialogComponent, {
        data: { mode: 'create', roles: this.sharedData.roles$() ?? [] } as IAddCustomRoleDialogData,
        disableClose: true,
        // Explicit `width` (not just `minWidth`) - the permission-toggle grid's flex-wrap content
        // otherwise drives the dialog's auto width well past 700px.
        width: '700px',
      })
      .afterClosed()
      .subscribe((role) => {
        if (role) {
          this.sharedData.upsertRole(role);
        }
      });
  }

  public onAddTeamClick(): void {
    this.dialog
      .open<AddTeamDialogComponent, undefined, ITeam | undefined>(AddTeamDialogComponent, {
        disableClose: true,
        minWidth: '700px',
      })
      .afterClosed()
      .subscribe((team) => {
        if (team) {
          this.sharedData.upsertTeam(team);
        }
      });
  }

  /** A role is opened read-only ('view' mode) for two different reasons, distinguished by
   * `isProtected` in the dialog data: a true system role (Admin/Member, `row.isProtected`) can
   * never be edited by anyone, so the dialog shows its "System role" badge/caption; a role broader
   * than the current user's own permissions, or one the user can only read but not update
   * (`!canEditRole`), is *also* opened read-only - without that badge/caption, since it isn't
   * actually a protected system role (see AddCustomRoleDialogComponent's `isProtected` input).
   * A custom role additionally requires `roleReadOwn` or `roleUpdateOwn` just to open the dialog
   * at all (canViewRole) - a protected role has no such requirement, matching its ungated menu
   * item in the template. */
  public onRoleMenuClick(row: IUserManagementRoleTeamRow): void {
    if (!row.role || (!row.isProtected && !this.canViewRole())) {
      return;
    }

    const { role } = row;
    const mode = row.isProtected || !this.canEditRole(row) ? 'view' : 'edit';

    this.dialog
      .open<AddCustomRoleDialogComponent, IAddCustomRoleDialogData, IRole | undefined>(AddCustomRoleDialogComponent, {
        data: { isProtected: row.isProtected, mode, role } as IAddCustomRoleDialogData,
        disableClose: true,
        width: '700px',
      })
      .afterClosed()
      .subscribe((updatedRole) => {
        if (updatedRole) {
          this.sharedData.upsertRole(updatedRole);
        }
      });
  }

  /** Single handler for both tables' `(rowClick)` - dispatches to whichever dialog-opening method
   * the menu's own equivalent item would use, so a row click always "opens their edit/view dialog
   * accordingly" (same mode-determination logic either way). The 3-dot menu trigger button stops
   * propagation, so clicking it never also fires this. Unlike the menu's own item, a row click
   * bypasses the `*hasPermission` directive entirely, so onRoleMenuClick/onEditTeamClick below
   * each re-check the same permission themselves - this handler doesn't need its own guard. */
  public onRoleTeamRowClick(event: IRowClickEvent<IUserManagementRoleTeamRow, string>): void {
    const { row } = event;

    if (row.kind === 'role') {
      this.onRoleMenuClick(row);
    } else {
      this.onEditTeamClick(row);
    }
  }

  public onEditTeamClick(row: IUserManagementRoleTeamRow): void {
    if (!row.team || !this.permissionService.hasPermission(this.EPermissions.teamUpdateOwn)) {
      return;
    }

    const { team } = row;

    this.dialog
      .open<AddTeamDialogComponent, IAddTeamDialogData, ITeam | undefined>(AddTeamDialogComponent, {
        data: { team } as IAddTeamDialogData,
        disableClose: true,
        minWidth: '700px',
      })
      .afterClosed()
      .subscribe((updatedTeam) => {
        if (updatedTeam) {
          this.sharedData.upsertTeam(updatedTeam);
        }
      });
  }

  /** A role can only be edited or deleted by someone who already holds every permission it
   * grants - otherwise this would let a user modify or remove a role whose full extent they can't
   * even evaluate themselves (mirrors canManageUserRole/isRoleAssignable's reasoning elsewhere in
   * this feature). Used to gate Delete (see getRoleDeleteBlockedMessage) - deliberately does NOT
   * also check `roleUpdateOwn` there, since Delete already has its own independent
   * `EPermissions.roleDeleteOwn` gate on the button and shouldn't additionally require update
   * rights. For the edit/view MODE decision, see canEditRole below instead, which layers the
   * actual update permission on top of this same permission-superset check. */
  public canManageRole(row: IUserManagementRoleTeamRow): boolean {
    return !!row.role && !roleExceedsPermissions(row.role, this.currentUserPermissions());
  }

  /** Whether the current user is allowed to actually EDIT (not just view) this role - requires
   * both `roleUpdateOwn` (the real update permission) and canManageRole (the role's own
   * permissions don't exceed the current user's). A user holding only `roleReadOwn` must always
   * land in 'view' mode regardless of canManageRole, which is why this needs to be a separate
   * check from canManageRole rather than folded into it (canManageRole is also reused by the
   * delete-blocked-message logic, which must NOT start requiring roleUpdateOwn - see its own doc
   * comment). */
  public canEditRole(row: IUserManagementRoleTeamRow): boolean {
    return this.canManageRole(row) && this.permissionService.hasPermission(this.EPermissions.roleUpdateOwn);
  }

  /** Whether the current user should be able to open a custom (non-protected) role's dialog at
   * all, in either mode - `roleReadOwn` grants view-only access, `roleUpdateOwn` implies it.
   * Protected (built-in) roles have no permission requirement to view at all (see the template's
   * ungated `row.isProtected` branch), so this is only relevant for custom roles. */
  private canViewRole(): boolean {
    return this.permissionService.hasAnyPermission([this.EPermissions.roleReadOwn, this.EPermissions.roleUpdateOwn]);
  }

  /** Shown as a tooltip on the role row's Delete menu item, and used to guard onDeleteRoleClick.
   * Checked in priority order: a role broader than the current user's own permissions can't be
   * deleted at all (canManageRole - same reasoning as editing it); then a role that still has
   * joined members can't be deleted either, since that would leave those members without a role;
   * then a role with no joined members but at least one still-open (pending/expired) invitation
   * can't be deleted either - see IUserManagementRoleTeamRow.pendingInvitationCount's doc comment
   * for why deleting it anyway would silently orphan that invitation's roleId. */
  public getRoleDeleteBlockedMessage(row: IUserManagementRoleTeamRow): string | null {
    if (!this.canManageRole(row)) {
      return this.translate.instant(
        'pages.settings.userManagement.rolesTeamsTab.cannotDeleteRolePermissionMessage',
      ) as string;
    }

    if (row.userCount > 0) {
      return this.translate.instant('pages.settings.userManagement.rolesTeamsTab.roleHasMembersMessage') as string;
    }

    if (row.pendingInvitationCount > 0) {
      return this.translate.instant(
        'pages.settings.userManagement.rolesTeamsTab.roleHasPendingInvitationsMessage',
      ) as string;
    }

    return null;
  }

  /** Shown as a tooltip on the team row's Delete menu item, and used to guard onDeleteTeamClick -
   * teams have no permission-based gating (only roles do), just the member/invitation-count
   * checks (see getRoleDeleteBlockedMessage's doc comment for the invitation-count reasoning,
   * identical here). */
  public getTeamDeleteBlockedMessage(row: IUserManagementRoleTeamRow): string | null {
    if (row.userCount > 0) {
      return this.translate.instant('pages.settings.userManagement.rolesTeamsTab.teamHasMembersMessage') as string;
    }

    if (row.pendingInvitationCount > 0) {
      return this.translate.instant(
        'pages.settings.userManagement.rolesTeamsTab.teamHasPendingInvitationsMessage',
      ) as string;
    }

    return null;
  }

  /** Optimistic: the row disappears from the shared roles list the instant the confirm dialog
   * resolves, rather than waiting on the delete request. If the request then fails there's no
   * per-row data left to roll back to (the row is already gone from every consumer of
   * `sharedData.roles$()`, including the Users tab's role filter/pills), so the only correct
   * recovery is reloading the shared roles list from the server. */
  public onDeleteRoleClick(row: IUserManagementRoleTeamRow): void {
    if (!row.role || this.getRoleDeleteBlockedMessage(row)) {
      return;
    }

    const roleId = row.role.id;

    this.confirmDelete(
      this.translate.instant('pages.settings.userManagement.rolesTeamsTab.deleteRoleMessage', { name: row.name }),
    ).subscribe((confirmed) => {
      if (!confirmed) {
        return;
      }

      this.sharedData.removeRole(roleId);
      this.sharedData.markRoleDeletePending(roleId);

      this.roleService.deleteRole(roleId).subscribe({
        error: () => {
          this.snackbarService.openError(
            this.translate.instant('pages.settings.userManagement.rolesTeamsTab.deleteRoleError'),
          );
          // Unmark before reloading - this row genuinely wasn't deleted, so it's fine (expected,
          // even) for this row's own reload to bring it back.
          this.sharedData.clearRoleDeletePending(roleId);
          this.sharedData.reloadRoles();
        },
        next: () => {
          this.sharedData.clearRoleDeletePending(roleId);
        },
      });
    });
  }

  /** Optimistic: same reasoning as onDeleteRoleClick above, for teams. */
  public onDeleteTeamClick(row: IUserManagementRoleTeamRow): void {
    if (!row.team || this.getTeamDeleteBlockedMessage(row)) {
      return;
    }

    const teamId = row.team.id;

    this.confirmDelete(
      this.translate.instant('pages.settings.userManagement.rolesTeamsTab.deleteTeamMessage', { name: row.name }),
    ).subscribe((confirmed) => {
      if (!confirmed) {
        return;
      }

      this.sharedData.removeTeam(teamId);
      this.sharedData.markTeamDeletePending(teamId);

      this.teamService.deleteTeam(teamId).subscribe({
        error: () => {
          this.snackbarService.openError(
            this.translate.instant('pages.settings.userManagement.rolesTeamsTab.deleteTeamError'),
          );
          // Unmark before reloading - this row genuinely wasn't deleted, so it's fine (expected,
          // even) for this row's own reload to bring it back.
          this.sharedData.clearTeamDeletePending(teamId);
          this.sharedData.reloadTeams();
        },
        next: () => {
          this.sharedData.clearTeamDeletePending(teamId);
        },
      });
    });
  }

  /** For a joined member, `roles` always ends with that user's role for *this* group - same
   * convention as the Users tab's `allRows` computed, see that component's doc comment. */
  private membershipRoleId(user: IUser): string | null {
    return user.roles.length ? user.roles[user.roles.length - 1].id : null;
  }

  private toAvatars(users: IUser[]): IIconData[] {
    return users.map((user) => ({
      alt: user.name ?? '',
      content: user.iconId ?? '',
      id: parseInt(user.id.slice(0, 8), 16),
      name: user.name ?? '',
    }));
  }

  private confirmDelete(message: string): Observable<boolean | undefined> {
    return this.dialog
      .open<ConfirmDialogComponent, IConfirmDialogData, boolean>(ConfirmDialogComponent, {
        data: {
          cancelText: this.translate.instant('shared.cancel'),
          confirmText: this.translate.instant('shared.form.delete'),
          message,
          title: this.translate.instant('shared.form.delete'),
        } as IConfirmDialogData,
        width: '400px',
      })
      .afterClosed();
  }
}
